import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Info, X, MapPin } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const RelocationModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { theme } = useTheme();

  useEffect(() => {
    // Check if the user has already seen the modal in this session
    const hasSeenModal = sessionStorage.getItem('hasSeenRelocationModal');
    if (!hasSeenModal) {
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 1000); // Show after 1 second
      return () => clearTimeout(timer);
    }
  }, []);

  const closeModal = () => {
    setIsOpen(false);
    sessionStorage.setItem('hasSeenRelocationModal', 'true');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
            className="absolute inset-0 bg-black/70 backdrop-blur-md"
          />

          {/* Modal */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className={`relative w-full max-w-lg overflow-hidden rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.3)] ${
              theme === 'dark' 
                ? 'bg-gray-900/90 border border-gray-800' 
                : 'bg-white/95 border border-gray-100'
            } backdrop-blur-xl`}
          >
            {/* Top accent bar with gradient */}
            <div className="h-2 bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600 w-full" />

            <div className="p-6 sm:p-10">
              {/* Close Button */}
              <button
                onClick={closeModal}
                className={`absolute top-5 right-5 p-2 rounded-full transition-all duration-300 ${
                  theme === 'dark' 
                    ? 'hover:bg-gray-800 text-gray-400 hover:text-white' 
                    : 'hover:bg-gray-100 text-gray-500 hover:text-gray-900'
                }`}
              >
                <X className="w-5 h-5" />
              </button>

              <div className="flex flex-col items-center text-center">
                <motion.div 
                  initial={{ rotate: -10, scale: 0.9 }}
                  animate={{ rotate: 0, scale: 1 }}
                  transition={{ delay: 0.2, type: "spring" }}
                  className={`mb-6 p-5 rounded-2xl ${
                    theme === 'dark' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-emerald-50 text-emerald-600'
                  }`}
                >
                  <MapPin className="w-12 h-12" />
                </motion.div>

                <h2 className={`text-3xl sm:text-4xl font-bold mb-4 font-display tracking-tight ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  We are Relocating!
                </h2>

                <p className={`text-lg sm:text-xl mb-4 leading-relaxed font-sans ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  To serve you better, we are currently moving to a new location. 
                  As a result, <span className="text-emerald-500 font-bold underline decoration-emerald-500/30 underline-offset-4">online ordering is temporarily unavailable</span>.
                </p>

                <p className={`text-base sm:text-lg mb-8 font-medium ${
                  theme === 'dark' ? 'text-emerald-400' : 'text-emerald-700'
                }`}>
                  We are still accepting <span className="font-bold">Party Orders</span> and <span className="font-bold">Takeaway Orders</span> via phone.
                </p>

                <div className={`w-full p-6 rounded-2xl border-2 border-emerald-500/20 relative overflow-hidden group transition-all duration-500 ${
                  theme === 'dark' 
                    ? 'bg-emerald-500/5' 
                    : 'bg-emerald-50/50'
                }`}>
                  {/* Subtle background glow */}
                  <div className="absolute inset-0 bg-emerald-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <p className={`text-xs font-bold mb-3 uppercase tracking-[0.2em] relative z-10 ${
                    theme === 'dark' ? 'text-emerald-400' : 'text-emerald-700'
                  }`}>
                    Order via Phone Only
                  </p>
                  
                  <a
                    href="tel:07521262119"
                    className={`flex items-center justify-center gap-4 text-3xl sm:text-4xl font-black relative z-10 transition-transform duration-300 hover:scale-[1.02] ${
                      theme === 'dark' ? 'text-white' : 'text-emerald-950'
                    }`}
                  >
                    <div className="p-2 bg-emerald-500 rounded-lg text-white shadow-lg shadow-emerald-500/20">
                      <Phone className="w-6 h-6 fill-current" />
                    </div>
                    07521 262119
                  </a>
                </div>

                <div className="mt-10 flex flex-col sm:flex-row gap-4 w-full">
                  <button
                    onClick={closeModal}
                    className="flex-1 px-8 py-4 bg-emerald-600 text-white font-bold rounded-2xl hover:bg-emerald-700 transition-all shadow-[0_10px_20px_rgba(16,185,129,0.2)] hover:shadow-[0_15px_25px_rgba(16,185,129,0.3)] active:scale-95 text-lg"
                  >
                    I Understand
                  </button>
                </div>
                
                <p className={`mt-6 text-sm italic font-medium ${
                  theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                }`}>
                  Thank you for your patience and continued support.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default RelocationModal;
